import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GamePlayUI from '../components/GamePlayUI';
import GameResults from '../components/GameResults';
import { toast } from '../components/Toast';
import { buildShareText, copyText, colorFor } from '../components/ShareResults';
import './DailyChallengePage.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const DAILY_GUEST_KEY = 'dailyGuestId';

// Visitors who are not signed in and not in guest mode still get a stable id for the daily run.
// It lives under its own key so it never switches the rest of the app into guest mode.
const dailyGuestId = () => {
  try {
    let id = localStorage.getItem(DAILY_GUEST_KEY);
    if (!id || !/^[A-Za-z0-9_-]{6,64}$/.test(id)) {
      id = `daily_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DAILY_GUEST_KEY, id);
    }
    return id;
  } catch (_) {
    return `daily_${Math.random().toString(36).slice(2, 12)}`;
  }
};

const longDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

const fmt = (s) => {
  if (typeof s !== 'number') return '-';
  const t = Math.floor(s);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

function Mark() {
  return <span className="daily-mark" aria-hidden="true"><i /><i /><i /><i /></span>;
}

const DailyChallengePage = () => {
  const navigate = useNavigate();
  const { token, isGuest, guestId } = useAuth();
  const [view, setView] = useState('loading'); // loading | error | landing | countdown | playing | done
  const [challenge, setChallenge] = useState(null);
  const [run, setRun] = useState(null);         // { startedAt (local ms), lives, foundGroups, guesses }
  const [final, setFinal] = useState(null);
  const [board, setBoard] = useState(null);     // leaderboard response
  const [count, setCount] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const timers = useRef([]);

  const identity = useCallback(() => {
    if (token) return { headers: { 'x-auth-token': token }, body: {} };
    const gid = isGuest && guestId ? guestId : dailyGuestId();
    return { headers: { 'x-guest-id': gid }, body: { guestId: gid } };
  }, [token, isGuest, guestId]);

  const request = useCallback(async (method, path, body) => {
    const id = identity();
    const res = await fetch(`${API}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...id.headers },
      body: method === 'GET' ? undefined : JSON.stringify({ ...id.body, ...(body || {}) }),
    });
    let data = null;
    try { data = await res.json(); } catch (_) { data = null; }
    return { ok: res.ok, status: res.status, data };
  }, [identity]);

  const loadBoard = useCallback(async () => {
    const r = await request('GET', '/api/daily/today/leaderboard?limit=10');
    if (r.ok) setBoard(r.data);
  }, [request]);

  const applyRun = (me) => {
    const offset = (me.serverNow || Date.now()) - Date.now();
    setRun({
      startedAt: new Date(me.startedAt).getTime() - offset,
      lives: me.lives,
      foundGroups: me.foundGroups || [],
      guesses: me.guesses || [],
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await request('GET', '/api/daily/today');
        if (cancelled) return;
        if (!r.ok) throw new Error('load failed');
        setChallenge(r.data);
        const me = r.data.me;
        if (me && me.status === 'done') {
          applyRun(me);
          setFinal(me.result);
          setShowResults(true);
          setView('done');
          loadBoard();
        } else if (me) {
          applyRun(me);
          setView('playing');
        } else {
          setView('landing');
        }
      } catch (_) {
        if (!cancelled) setView('error');
      }
    })();
    return () => { cancelled = true; };
    // identity changes (sign in or out) reload the page state
  }, [request, loadBoard]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const start = () => {
    setView('countdown');
    [3, 2, 1].forEach((n, i) => timers.current.push(setTimeout(() => setCount(n), i * 900)));
    timers.current.push(setTimeout(async () => {
      setCount(null);
      const r = await request('POST', '/api/daily/today/start');
      if (!r.ok) {
        toast((r.data && r.data.message) || 'Could not start the daily challenge', { error: true });
        setView('landing');
        return;
      }
      applyRun(r.data);
      if (r.data.status === 'done') {
        setFinal(r.data.result);
        setShowResults(true);
        setView('done');
        loadBoard();
      } else {
        setView('playing');
      }
    }, 2700));
  };

  const submitGuess = useCallback(async (words) => {
    let r;
    try {
      r = await request('POST', '/api/daily/today/guess', { words });
    } catch (_) {
      return { type: 'error', message: 'Could not reach the server. Try again.' };
    }
    if (r.status === 409 && r.data && r.data.result) {
      setFinal(r.data.result);
      setView('done');
      loadBoard();
      return { type: 'rejected', message: 'You already played today' };
    }
    if (!r.ok || !r.data) return { type: 'error', message: (r.data && r.data.message) || 'Something went wrong' };
    const d = r.data;
    if (d.result === 'rejected') return { type: 'rejected', message: d.message };
    setRun((prev) => ({
      ...prev,
      lives: d.lives,
      guesses: [...prev.guesses, words],
      foundGroups: d.group ? [...prev.foundGroups, d.group] : prev.foundGroups,
    }));
    if (d.done) {
      setFinal(d.final);
      setView('done');
      loadBoard();
    }
    return d.result === 'correct'
      ? { type: 'correct', foundCount: (run ? run.foundGroups.length : 0) + 1 }
      : { type: 'incorrect', oneAway: d.oneAway, lives: d.lives };
  }, [request, loadBoard, run]);

  if (view === 'loading') {
    return <div className="cr-page"><p className="cr-muted daily-center">Loading today's puzzle</p></div>;
  }
  if (view === 'error') {
    return (
      <div className="cr-page">
        <section className="cr-card cr-card-pad daily-card">
          <h1 className="cr-title">Daily Challenge</h1>
          <p className="cr-muted">Could not load today's puzzle. Check your connection and try again.</p>
          <button type="button" className="cr-btn solid daily-start" onClick={() => window.location.reload()}>Try Again</button>
        </section>
      </div>
    );
  }

  const date = challenge && challenge.date;
  const stats = (board && board.stats) || (challenge && challenge.stats) || {};

  if (view === 'landing' || view === 'countdown') {
    return (
      <div className="cr-page">
        <section className="cr-card daily-card">
          <Mark />
          <div>
            <h1 className="cr-title">Daily Challenge</h1>
            <p className="cr-muted daily-date">{longDate(date)}</p>
          </div>
          <div className="daily-stats">
            <div className="cr-stat"><b>{stats.totalAttempts || 0}</b><span>{stats.totalAttempts === 1 ? 'player today' : 'players today'}</span></div>
            <div className="cr-stat"><b>{stats.totalAttempts ? `${stats.solveRate}%` : '-'}</b><span>solve rate</span></div>
          </div>
          <button type="button" className="cr-btn solid daily-start" onClick={start} disabled={view === 'countdown'}>
            Start Daily Challenge
          </button>
          <p className="cr-label">Solve today's puzzle and see how you rank. You can only play once per day.</p>
        </section>
        {count != null && (
          <div className="daily-count-wrap"><div className="daily-count" key={count} aria-live="assertive">{count}</div></div>
        )}
      </div>
    );
  }

  const endedAt = final && run ? run.startedAt + (final.completionTime || 0) * 1000 : null;

  if (view === 'done' && showResults && final) {
    const me = board && board.me;
    const rank = (me && me.rank) || final.rank;
    const total = (me && me.totalPlayers) || final.totalPlayers;
    const better = me && me.betterThan !== undefined ? me.betterThan : final.betterThan;
    const history = (final.history || []).map((h) => h.levels);
    const foundLevels = run ? run.foundGroups.map((g) => g.level) : [];
    const missed = (final.solution || []).filter((g) => !foundLevels.includes(g.level));
    const rows = ((board && board.leaderboard) || []).map((e) => ({
      key: `r${e.rank}`, rank: e.rank, name: e.username, color: colorFor(e.username), isYou: e.isMe,
      time: e.completionTime, solved: e.solved, found: e.categoriesFound,
    }));
    if (me && !rows.some((r) => r.isYou)) {
      rows.push({
        key: 'me', rank: me.rank, name: me.username, color: colorFor(me.username), isYou: true,
        time: me.completionTime, solved: me.solved, found: me.categoriesFound, gap: true,
      });
    }
    const share = buildShareText({
      title: 'Connections Racer',
      subtitle: `Daily Challenge ${date}`,
      solved: final.solved,
      time: final.completionTime,
      history,
    });
    return (
      <GameResults
        label={`Daily Challenge, ${longDate(date)}`}
        title={final.solved ? 'Puzzle Solved!' : 'Out of Lives'}
        stats={[
          { label: 'Time', value: final.solved ? fmt(final.completionTime) : '-' },
          { label: 'Rank', value: rank || '-', suffix: total ? `/ ${total}` : undefined },
          { label: 'Mistakes', value: final.mistakes ?? 0 },
          better != null
            ? { label: `Better than ${better}% of players`, value: `${better}%` }
            : { label: 'Only player so far', value: '-' },
        ]}
        table={{ title: "Today's Leaderboard", kind: 'daily', rows }}
        history={history}
        missed={missed}
        onCopy={() => copyText(share, 'Results copied')}
        actions={<button type="button" className="cr-btn" onClick={() => navigate('/')}>Back to Home</button>}
      />
    );
  }

  return (
    <GamePlayUI
      label="Daily Challenge"
      words={(challenge && challenge.puzzle.words) || []}
      foundGroups={run ? run.foundGroups : []}
      guesses={run ? run.guesses : []}
      lives={run ? run.lives : 4}
      startedAt={run ? run.startedAt : null}
      endedAt={endedAt}
      locked={view === 'done'}
      onSubmit={submitGuess}
      footer={view === 'done' ? (
        <div className="race-footer">
          <div className="race-footer-actions">
            <button type="button" className="cr-btn solid" onClick={() => setShowResults(true)}>See Results</button>
          </div>
        </div>
      ) : null}
    />
  );
};

export default DailyChallengePage;
